import { convertSummaryValue } from "@/core/utils/formatters"

export const generateSummary = (schema, data) => {
  const selectedValues = data?.value || data || []

  return schema.checkboxes
    .filter((checkbox) => selectedValues.includes(checkbox.value))
    .map((checkbox) => {
      // Case 1: Radio group
      if (checkbox.radioGroup) {
        const selectedRadio =
          data[checkbox.radioGroup.key] ?? checkbox.radioGroup.default
        return `${checkbox.label}\n${selectedRadio}`
      }

      // Case 2: Per-checkbox input field
      if (checkbox.input?.isInput) {
        const inputText = data[checkbox.input.key] ?? ""
        return `${checkbox.label}\n${inputText}`
      }

      // Case 3: Group-level input field tied to a checkbox
      if (schema.input && checkbox.value === schema.input.const) {
        const inputText = data[schema.input.key] ?? ""
        return `${checkbox.label}\n${inputText}`
      }

      // Case 4: Plain checkbox
      return checkbox.label
    })
    .join("\n")
}

export const generateSummarySchema = (
  prescriptionSchema: any,
  templateSchema: any,
) => {
  const props = {
    ...prescriptionSchema.properties,
    ...templateSchema?.properties,
  }
  const defs = {
    ...prescriptionSchema.definitions,
    ...templateSchema?.definitions,
  }

  const newProps = {}

  for (const prop in props) {
    newProps[prop] = {
      ...props[prop],
      summary: true,
    }
  }
  const newDefs = {}
  for (const def in defs) {
    newDefs[def] = {
      ...defs[def],
      summary: true,
    }
  }

  return {
    ...prescriptionSchema,
    properties: newProps,
    definitions: newDefs,
  }
}

function recursiveUiSchemaFlattening(sourceUiSchema: any, prescriptionData: any) {
  const filteredElements: any[] = [];

  sourceUiSchema?.elements?.forEach((element: any) => {
    if (element?.type === "Group" && Array.isArray(element.elements)) {
      const subGroupElements: any[] = [];
      element.elements.forEach((item: any) => {
        const propName = item?.scope?.split("/properties/")[1];
        if (propName && propName in prescriptionData) {
          subGroupElements.push(item);
        }
      });
      if (subGroupElements.length > 0) {
        filteredElements.push({
          type: "Group",
          label: element.label,
          elements: subGroupElements,
        });
      }
    } else if (element?.type === "Control" || element?.type === "isRadioGroup") {
      const propName = element?.scope?.split("/properties/")[1];
      if (propName && propName in prescriptionData) {
        filteredElements.push(element);
      }
    } else if (element?.type === "VerticalLayout") {
      const flattenedLayout = recursiveUiSchemaFlattening(element, prescriptionData);
      if (flattenedLayout.elements.length > 0) {
        filteredElements.push(
          element?.rule
            ? {
                rule: element?.rule,
                type: "VerticalLayout",
                elements: flattenedLayout.elements,
              }
            : {
                type: "VerticalLayout",
                elements: flattenedLayout.elements,
              },
        )
      }
    }
  });

  return {
    type: sourceUiSchema.type,
    label: sourceUiSchema.label,
    elements: filteredElements,
  };
}

export const generateSummaryUiSchema = (
  prescriptionUiSchema: any,
  prescriptionData: any,
  otherformsUI: any,
  isPdf: boolean,
) => {

  let prescriptionSchema = {
    type: "Group",
    label: "Review Prescription",
    elements: [],
  }

  const filteredPrescriptionElements = recursiveUiSchemaFlattening(prescriptionUiSchema, prescriptionData).elements;
  prescriptionSchema.elements = filteredPrescriptionElements;
  const targetLabel = `${prescriptionData.smart_rx_template} Instructions`

  let matchedRule = null

  for (const element of prescriptionUiSchema.elements ?? []) {
    if (!element.rule || !Array.isArray(element.elements)) continue

    const hasMatchingLabel = element.elements.some(
      (child) => child.label === targetLabel,
    )

    if (hasMatchingLabel) {
      matchedRule = element.rule
      break
    }
  }

  const otherFormUISchema = {
    type: isPdf ? "Group" : "AccordionGroup",
    label: prescriptionData.smart_rx_template + " smartrx template",
    elements: [],
    rule: matchedRule
  }

  if (Array.isArray(otherformsUI?.elements)) {
    for (const element of otherformsUI.elements) {
      if (element?.type === "Group") {
        otherFormUISchema.elements.push(element)
      }
    }
  }

  // Conditionally add otherFormUISchema only if label is valid
  const resElements = [prescriptionSchema]
  if (prescriptionData.smart_rx_template) {
    resElements.push(otherFormUISchema)
  }

  return {
    type: "VerticalLayout",
    elements: resElements,
  }
}

export const generateSummaryData = (defaultdata: any) => {
  
  if (defaultdata.wear_time_of_aligners) {
    if (defaultdata.wear_time_of_aligners.value === "custom") {
      defaultdata.wear_time_of_aligners = convertSummaryValue(
        defaultdata.wear_time_of_aligners.stage,
        " Days",
      )
    }
  }
  if (defaultdata.smile_design?.stage) {
    defaultdata.smile_design = {
      ...defaultdata.smile_design,
      stage: defaultdata.smile_design?.stage + " mm",
    }
  }
  if (defaultdata.attachments?.value === "Yes") {
    defaultdata.attachments = {
      ...defaultdata.attachments,
      stage: "Stage: " + defaultdata.attachments.stage,
    }
  }
  return defaultdata
}